import React, { useMemo } from 'react';
import { SearchBar, TitleBar, DropDown, PaginatedTable } from '../../common';
import {
  BUTTON,
  DISPATCH_COLUMN,
  ROUTES,
  TITLES,
  LOCAL_STORAGE,
  VENDOR,
} from '../../../constants';
import { dispatchDataCompiler, getLocalStorageItem } from '../../../utils';
import { Button, InputWithSearch } from '../../shared';
import {
  useDispatchUrlState,
  useDispatchFilters,
  useDispatchGroupBy,
  useDispatchPagination,
  useDispatchData,
  useDispatchSelection,
  useDispatchActions,
} from './hooks';
import { DispatchActionBar } from './components';

/**
 * FabricDispatchContent - Dispatch content component for fabric and dyeing vendors
 *
 * Features:
 * - GROUP_DISPATCH button
 * - PRINT_PACKING_LIST button
 * - CREATE_BILL button
 * - No CREATE_DISPATCH button
 * - No Container No input
 * - Columns: Show Lot Color/Size, Rib Color/Size (fabric); Show Finished Product (dyeing); Hide Container No
 * - Table class: table-1
 * - Detail route: FABRIC_DISPATCH_DETAIL
 */
export const FabricDispatchContent = () => {
  const vendorType = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);
  const isDyeing = vendorType === VENDOR.DYE;
  const isExcludedVendor = vendorType === 'yarn' || vendorType === 'finished_goods';

  // Column configuration for fabric/dyeing vendor
  const columnConfig = useMemo(() => {
    if (isDyeing) {
      return {
        show: ['Finished Product'],
        hide: [
          'Container No',
          'Lot Color',
          'Lot Size',
          'Rib Color',
          'Rib Size',
          ...(isExcludedVendor ? ['Bill Status'] : []),
        ],
      };
    }
    // Fabric vendor
    return {
      show: ['Lot Color', 'Lot Size', 'Rib Color', 'Rib Size'],
      hide: ['Finished Product', 'Container No', ...(isExcludedVendor ? ['Bill Status'] : [])],
    };
  }, [isDyeing, isExcludedVendor]);

  // Selection filters for fabric/dyeing vendor
  const selectionFilters = useMemo(
    () => ({
      printMode: () => true, // All rows selectable
      billMode: (row) =>
        row.dispatch_status === 'draft' &&
        (row?.partner_id?.vendor_type === 'dyeing' || row?.vendor_id?.vendor_type),
      createBillMode: (row) =>
        (row.dispatch_status === 'dispatched' || row.dispatch_status === 'received') &&
        !row.dispatch_bill_id,
    }),
    [],
  );

  // =====================================================================
  // COMPOSE HOOKS
  // =====================================================================

  // URL state management
  const urlState = useDispatchUrlState();

  // Filter state (search + sort)
  const filters = useDispatchFilters({
    initialState: urlState.initialState,
  });

  // GroupBy state
  const groupByState = useDispatchGroupBy({
    initialState: urlState.initialState,
    searchParams: urlState.searchParams,
  });

  // Pagination state
  const pagination = useDispatchPagination({
    initialPage: urlState.initialState.pageNumber,
  });

  // Data fetching (orchestrates effects)
  const data = useDispatchData({
    filters,
    groupByState,
    pagination,
    urlState,
  });

  // Selection state
  const selection = useDispatchSelection({
    dispatchData: data.dispatchData,
    paginationData: pagination.paginationData,
    selectableRecords: pagination.selectableRecords,
    printPackingSelectableRecords: pagination.printPackingSelectableRecords,
    createBillSelectableRecords: pagination.createBillSelectableRecords,
    columnConfig,
    selectionFilters,
  });

  // Actions
  const actions = useDispatchActions({
    selection,
    filters,
    groupByState,
    data,
    detailRoute: ROUTES.FABRIC_DISPATCH_DETAIL,
    postSubmitBehavior: 'createBill', // After submit, transition to create bill mode
    showContainerInput: false,
  });

  // =====================================================================
  // DESTRUCTURE FOR TEMPLATE
  // =====================================================================

  const {
    searchTerm,
    setSearchTerm,
    search,
    setSearch,
    dispatchStatusFilter,
    setDispatchStatusFilter,
    stateFilter,
    setStateFilter,
    billStatusFilter,
    setBillStatusFilter,
    dispatchStatusOptions,
    stateOptions,
    billStatusOptions,
    resetFilters,
  } = filters;

  const {
    groupArray,
    groupBy,
    setGroupBy,
    availableValuesGroupBy,
    setAvailableValuesGroupBy,
    selectedItem,
    setSelectedItem,
    removeGroupBy,
  } = groupByState;

  const { pageNumber, setPageNumber } = pagination;

  const { dispatchData, isLoading, getDispatchList } = data;

  const {
    billMode,
    printMode,
    createBillMode,
    filteredColumn,
    addPOToBill,
    checked,
    isBillMode,
    isPrintMode,
    isCreateBillMode,
  } = selection;

  const { detailRoute } = actions;

  const isAnyModeActive = billMode || printMode || createBillMode;

  // Handler for removeGroupBy that also fetches fresh data
  const handleRemoveGroupBy = () => {
    removeGroupBy({
      onRemove: () => {
        urlState.updateUrlParams({
          search: searchTerm,
          dispatchStatus:
            dispatchStatusFilter !== DISPATCH_COLUMN[16].name
              ? dispatchStatusFilter
              : '',
          state: stateFilter !== DISPATCH_COLUMN[17].name ? stateFilter : '',
          billStatus:
            billStatusFilter !== DISPATCH_COLUMN[15].name
              ? billStatusFilter
              : '',
          page: 1,
        });
        getDispatchList({
          page_number: 1,
          groupby_field: '',
          groupby_specifics: {},
        });
      },
    });
  };

  // Handler for reset filters that also fetches fresh data
  const handleResetFilters = () => {
    resetFilters();
    pagination.setPageNumber(1);
    getDispatchList({ page_number: 1 });
  };

  return (
    <>
      <TitleBar title={[{ text: TITLES.DISPATCH, route: ROUTES.DISPATCH }]} />
      <div className="d-flex align-items-center justify-content-start gap-3 w-100">
        <SearchBar
          search={search}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          setSearch={setSearch}
          onDefaultSearch={() => getDispatchList({ page_number: 1 })}
        />
        <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
          <DropDown
            dropdownMenu={dispatchStatusOptions}
            value={dispatchStatusFilter}
            setValue={setDispatchStatusFilter}
          />
          <DropDown
            dropdownMenu={stateOptions}
            value={stateFilter}
            setValue={setStateFilter}
          />
          {!isExcludedVendor && (
            <DropDown
              dropdownMenu={billStatusOptions}
              value={billStatusFilter}
              setValue={setBillStatusFilter}
            />
          )}
          {(dispatchStatusFilter !== DISPATCH_COLUMN[16].name ||
            stateFilter !== DISPATCH_COLUMN[17].name ||
            (!isExcludedVendor && billStatusFilter !== DISPATCH_COLUMN[15].name)) && (
            <Button
              clickHandler={handleResetFilters}
              className="blue-btn d-flex align-items-center justify-content-center px-3">
              {BUTTON.CLEAR_FILTERS}
            </Button>
          )}
        </div>
      </div>
      <div className="d-block align-items-center justify-content-between gap-3 w-100">
        <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
          <DropDown dropdownMenu={groupArray} value={groupBy} setValue={setGroupBy} />
          {groupArray.includes(groupBy) && (
            <InputWithSearch
              setAvailableItem={setAvailableValuesGroupBy}
              availableItem={availableValuesGroupBy}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
            />
          )}
          {groupArray.includes(groupBy) && (
            <Button
              clickHandler={handleRemoveGroupBy}
              className="blue-btn d-flex align-items-center justify-content-center px-3">
              {BUTTON.REMOVE}
            </Button>
          )}
        </div>

        {/* Action Bar - only shown when there's data */}
        {Array.isArray(dispatchData) && dispatchData?.length > 0 && (
          <DispatchActionBar
            selection={selection}
            actions={actions}
            selectableRecords={pagination.selectableRecords}
            features={{
              showGroupDispatch: true,
              showPrintPacking: true,
              showCreateBill: true,
              showCreateDispatch: false,
              showContainerInput: false,
            }}
          />
        )}
      </div>

      {/* Table */}
      {Array.isArray(dispatchData) ? (
        <PaginatedTable
          columns={filteredColumn}
          initialData={dispatchData}
          colFilter={true}
          to={isAnyModeActive ? '' : detailRoute}
          compiler={{ use: true, tool: dispatchDataCompiler }}
          func={{ addPOToBill, checked, isBillMode, isPrintMode, isCreateBillMode }}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          paginationData={pagination.paginationData}
          isLoading={isLoading}
          className="table-1"
        />
      ) : (
        <div>
          <h4 className="text-center px-1 py-5">No Records Found</h4>
        </div>
      )}
    </>
  );
};
